import { ReparoApi } from './index';
import { fetchPdfObjectUrl } from './pdf';
import { isManufacturerVendor, type VendorCapability } from './vendors';
import { walkPages } from './warehouse-ops';

// ─── Enums (mirror backend) ─────────────────────────────────────────────

export type WorkOrderStatus =
    | 'DRAFT'
    | 'ISSUED'
    | 'IN_PRODUCTION'
    | 'READY_TO_SHIP'
    | 'SHIPPED'
    | 'RECEIVED'
    | 'CANCELLED'
    /** @deprecated Legacy terminal status — RECEIVED replaces it. */
    | 'COMPLETED';

export type WorkOrderLocationType = 'WAREHOUSE' | 'VENDOR';

export type WorkOrderAllocationSourceType =
    | 'WAREHOUSE'
    | 'VENDOR'
    | 'INCOMING_PO';

// ─── Response shapes ────────────────────────────────────────────────────

export type WorkOrderAllocationSummary = {
    sourceType: WorkOrderAllocationSourceType;
    sourceId: string;
    sourceName: string;
    allocatedQty: number;
};

export type WorkOrderMaterial = {
    materialId: string;
    materialName: string;
    unit: string;
    requiredQty: number;
    allocatedQty: number;
    deficitQty: number;
    allocations: WorkOrderAllocationSummary[];
};

export type WorkOrder = {
    id: string;
    code: string;
    merchantId: string;
    productId: string;
    quantity: number;
    dueDate: string;
    productionLocationType: WorkOrderLocationType;
    productionLocationId: string;
    status: WorkOrderStatus;
    notes: string | null;
    createdBy: string;
    completedAt: string | null;
    cancelledAt: string | null;
    createdAt: string;
    updatedAt: string;
    /** The production order this line belongs to — every work order is a line of one. */
    productionOrderId?: string | null;
    /** Present on detail responses, omitted on list responses. */
    materials?: WorkOrderMaterial[];
    /**
     * Sum of FG inwards tagged to this WO. Present on list responses. Always 0
     * in V1 until the GRN-tagged completion flow ships.
     */
    receivedQuantity?: number;
    /** The rate agreed for this SKU, or null where none was stated. Only on an outsourced run. */
    unitPrice?: number | null;
    /** The GST slab on that rate. Null is "not stated", which is not the exempt 0%. */
    gstRate?: number | null;
};

export type WorkOrderListResponse = {
    data: WorkOrder[];
    meta: {
        page: number;
        take: number;
        itemCount: number;
        pageCount: number;
        hasPreviousPage: boolean;
        hasNextPage: boolean;
    };
};

// ─── Input shapes ───────────────────────────────────────────────────────

/**
 * A multi-SKU production order: the order handed to ONE manufacturer. The header carries
 * everything the lines must agree on (where, by when); each line is an ordinary work order.
 */
export type CreateProductionOrderLineInput = {
    productId: string;
    quantity: number;
    /** Defaults to the header's expectedCompletionDate. */
    dueDate?: string;
    notes?: string;

    /** The agreed rate for this finished good. Omitted on an in-house run. */
    unitPrice?: number;
    /** Omitted means no rate stated, which is NOT the exempt 0% slab. */
    gstRate?: number;
};

export type CreateProductionOrderInput = {
    /**
     * The number this order is known by. Omit to let the server allocate `PO-{year}-{n}`.
     *
     * Free-form on purpose: a brand arriving from another system has a numbering convention
     * of its own, and the document is the thing both sides quote. The server refuses a number
     * this merchant has already used, so the two schemes cannot collide silently.
     */
    code?: string;
    productionLocationType: WorkOrderLocationType;
    productionLocationId: string;
    expectedCompletionDate: string;
    lines: CreateProductionOrderLineInput[];
    notes?: string;
    keepExistingOpen?: boolean;

    // ─── The commercial document ──────────────────────────────────────────
    //
    // All omitted for an in-house run: no counterparty, nothing to bill, nothing to price.
    //
    // Subtotal, tax and total are deliberately absent — the server computes them from the
    // lines, so the arithmetic on the document cannot disagree with the lines above it.
    orderDate?: string;
    expiryDate?: string;
    currency?: string;
    additionalCharges?: Array<{ label: string; amount: number }>;
    termsAndConditions?: string;
    manufacturerName?: string;
    manufacturerEmail?: string;
    manufacturerAddress?: string;
    manufacturerGstin?: string;
    billToName?: string;
    billToAddress?: string;
    billToGstin?: string;
    shipToName?: string;
    shipToEmail?: string;
    shipToAddress?: string;
    shipToGstin?: string;
    /** Seeds the address fields and links a later dispatch; never replaces them. */
    shipToWarehouseId?: string;
    /**
     * Place of supply, as a GST state code — '06', not 'Haryana'.
     *
     * Picked rather than derived from the warehouse: `warehouses.state` is free text and mostly
     * blank, and a tax treatment must not turn on that.
     */
    shipToStateCode?: string;
    contactName?: string;
    contactEmail?: string;
};

export type ProductionOrder = {
    id: string;
    code: string;
    productionLocationType: WorkOrderLocationType;
    productionLocationId: string;
    expectedCompletionDate: string;
    status: string;
    lineCount?: number;
    totalQuantity?: number;
    /** Ids of the work orders opened as lines — needed to attach files per line. */
    workOrderIds?: string[];
    /** True once the order has been issued and its PDF stored. */
    hasDocument?: boolean;
    sentAt?: string | null;
    /** Who the order was emailed to, as it was at the time of sending. */
    sentTo?: string | null;
};

export type ListWorkOrdersParams = {
    page?: number;
    take?: number;
    status?: WorkOrderStatus;
};

// ─── API calls ──────────────────────────────────────────────────────────

export const createProductionOrder = async (
    payload: CreateProductionOrderInput,
): Promise<ProductionOrder> =>
    ReparoApi<ProductionOrder>({
        method: 'POST',
        url: '/work-orders/production-orders',
        body: JSON.stringify(payload),
    });

/**
 * A production order with the SKU lines beneath it — what the edit form opens on.
 *
 * The list row cannot serve this: it carries no rate or slab per line, and a form that opened
 * without them would blank every price the moment somebody fixed a quantity.
 */
export type ProductionOrderDetail = {
    order: ProductionOrder & {
        orderDate?: string | null;
        expiryDate?: string | null;
        currency?: string | null;
        notes?: string | null;
        termsAndConditions?: string | null;
        additionalCharges?: Array<{ label: string; amount: number }>;
        manufacturerName?: string | null;
        manufacturerEmail?: string | null;
        manufacturerAddress?: string | null;
        manufacturerGstin?: string | null;
        billToName?: string | null;
        billToAddress?: string | null;
        billToGstin?: string | null;
        shipToName?: string | null;
        shipToEmail?: string | null;
        shipToAddress?: string | null;
        shipToGstin?: string | null;
        shipToWarehouseId?: string | null;
        shipToStateCode?: string | null;
        contactName?: string | null;
        contactEmail?: string | null;
    };
    /** Live lines only; a cancelled SKU is not offered back. */
    lines: WorkOrder[];
};

export const getProductionOrder = async (orderId: string): Promise<ProductionOrderDetail> =>
    ReparoApi<ProductionOrderDetail>({
        method: 'GET',
        url: `/work-orders/production-orders/${orderId}`,
    });

/**
 * Edits to a DRAFT production order. A patch: absent leaves a field alone, NULL clears it.
 *
 * `lines`, when present, REPLACES the whole line set — a SKU left out is cancelled — so a caller
 * sends every line or none. The server refuses the call once the order has been sent.
 */
export type UpdateProductionOrderInput = {
    expectedCompletionDate?: string;
    orderDate?: string;
    currency?: string;
    notes?: string | null;
    termsAndConditions?: string | null;
    manufacturerName?: string | null;
    manufacturerEmail?: string | null;
    manufacturerAddress?: string | null;
    manufacturerGstin?: string | null;
    billToName?: string | null;
    billToAddress?: string | null;
    billToGstin?: string | null;
    shipToName?: string | null;
    shipToEmail?: string | null;
    shipToAddress?: string | null;
    shipToGstin?: string | null;
    shipToWarehouseId?: string | null;
    shipToStateCode?: string | null;
    contactName?: string | null;
    contactEmail?: string | null;
    lines?: CreateProductionOrderLineInput[];
};

export const updateProductionOrder = async (
    orderId: string,
    payload: UpdateProductionOrderInput,
): Promise<ProductionOrder> =>
    ReparoApi<ProductionOrder>({
        method: 'PATCH',
        url: `/work-orders/production-orders/${orderId}`,
        body: JSON.stringify(payload),
    });

/** One SKU beneath an order, shown when its row is expanded. */
export type ProductionOrderListLine = {
    workOrderId: string;
    code: string;
    productId: string;
    productName?: string | null;
    quantity: number;
    /**
     * Made against this line and still able to fill it: declared output, less anything that
     * failed QC or went out of date.
     *
     * NET, BECAUSE "STILL TO MAKE" SUBTRACTS IT. A gross figure calls a line finished when a
     * fifth of the run was written off — the site still owes those units, and the column that
     * tells them so reads `quantity - produced`.
     */
    produced: number;
    /** The LINE's status, which can differ from its order's. */
    status: string;
    dueDate: string | null;
    /**
     * What has actually left the site against this line, net of cancelled consignments.
     *
     * Counted from the per-batch movement rows, each of which names its own work order — the
     * only source that survives a lorry carrying batches against two different orders, since
     * such a consignment names neither on its shipment header.
     */
    dispatched: number;
    /**
     * Where the goods are now: cleared and still here, and declared but unruled. See the site
     * queue's own line for why neither is cumulative.
     */
    qcApproved: number;
    qcPending: number;
};

/**
 * A production order as a thing in its own right.
 *
 * The SKU-line list answers "what is being made"; this answers "what did we commission, has it
 * gone out, and what did they get" — facts that belong to the order and had no row to sit on
 * while the page was line-grained.
 */
export type ProductionOrderListRow = {
    id: string;
    code: string;
    productionLocationType: string;
    productionLocationId: string;
    productionLocationName: string;
    expectedCompletionDate: string;
    /** The ORDER's status, not a line's. */
    status: string;
    hasDocument: boolean;
    /** When the PDF was last rendered. Later than `sentAt` = edited after it went out. */
    documentGeneratedAt: string | null;
    sentAt: string | null;
    sentTo: string | null;
    /** Where it would go now, off the supplier record — what the send dialog pre-fills. */
    defaultRecipients: string[];
    /** When the order was PLACED — a different question from when it is due. */
    orderDate: string | null;
    /**
     * What the order is worth, GST included, as stored.
     *
     * A STRING, because it is a decimal column and parsing it here would be the one place money
     * loses precision. NULL IS NOT ZERO: an order raised before pricing existed has no value at
     * all, one priced at nothing has "0.00", and a list must not read the first as the second.
     */
    total: string | null;
    currency: string | null;
    lineCount: number;
    totalQuantity: number;
    lines: ProductionOrderListLine[];
};

/** Every production order, newest first, with its SKU lines for in-place expansion. */
export const listProductionOrders = async (): Promise<{
    orders: ProductionOrderListRow[];
}> =>
    ReparoApi({ method: 'GET', url: '/work-orders/production-orders' });

/**
 * Issue a production order: render it to PDF, keep the document, email it to the manufacturer.
 *
 * Explicit rather than automatic on creation, mirroring the purchase-order send — an order
 * raised from a replenishment run is worth a look before it becomes somebody's cutting list.
 */
export const sendProductionOrder = async (
    orderId: string,
    /**
     * Who this issue goes to. Omit and it goes to the supplier record's addresses.
     *
     * `to` REPLACES those addresses rather than adding to them — the dialog shows them
     * pre-filled and lets one be removed, so re-adding a deleted address would make the
     * confirmation say something untrue.
     */
    recipients?: { to?: string[]; cc?: string[]; withoutEmail?: boolean },
): Promise<ProductionOrder> =>
    ReparoApi<ProductionOrder>({
        method: 'POST',
        url: `/work-orders/production-orders/${orderId}/send`,
        ...(recipients ? { body: JSON.stringify(recipients) } : {}),
    });

/**
 * Cancel a production order and every SKU line under it that has not shipped.
 *
 * The server refuses once anything has moved; the message names the lines that did, and the
 * remaining ones are cancelled one at a time from the SKU grid instead.
 */
export const cancelProductionOrder = async (orderId: string): Promise<ProductionOrder> =>
    ReparoApi<ProductionOrder>({
        method: 'POST',
        url: `/work-orders/production-orders/${orderId}/cancel`,
        body: JSON.stringify({}),
    });

/**
 * A short-lived link to the issued document.
 *
 * The S3 key never reaches the browser — this returns a signed URL that expires, so a link
 * pasted into a chat stops working rather than becoming a permanent public copy.
 */
export const getProductionOrderDocumentUrl = async (
    orderId: string,
): Promise<{ url: string; expiresAt: string }> =>
    ReparoApi<{ url: string; expiresAt: string }>({
        method: 'GET',
        url: `/work-orders/production-orders/${orderId}/document`,
    });

/**
 * Open the order as it would print, before it is issued.
 *
 * Rendered on request and stamped DRAFT; nothing is stored, which is why this is a blob the
 * browser opens rather than the signed link an issued document gets.
 */
export const openProductionOrderPreview = async (orderId: string): Promise<void> => {
    const url = await fetchPdfObjectUrl(`/work-orders/production-orders/${orderId}/preview.pdf`);
    window.open(url, '_blank');
};

export const getWorkOrder = async (workOrderId: string): Promise<WorkOrder> =>
    ReparoApi<WorkOrder>({
        method: 'GET',
        url: `/work-orders/${workOrderId}`,
    });

export const listWorkOrders = async (
    params: ListWorkOrdersParams = {},
): Promise<WorkOrderListResponse> => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.take) qs.set('take', String(params.take));
    if (params.status) qs.set('status', params.status);
    const qsString = qs.toString();
    return ReparoApi<WorkOrderListResponse>({
        method: 'GET',
        url: `/work-orders${qsString ? `?${qsString}` : ''}`,
    });
};

/** Every SKU line, across pages: the lines tab searches and filters in the browser. */
export const listAllWorkOrders = (): Promise<WorkOrder[]> =>
    walkPages((page, take) => listWorkOrders({ page, take }));

export type PeggedPoSummary = {
    purchaseOrderId: string;
    status: string;
    vendorName: string | null;
    peggedQty: number;
    poDate: string | null;
};

export const getPeggedPosForWorkOrder = async (
    workOrderId: string,
): Promise<PeggedPoSummary[]> =>
    ReparoApi<PeggedPoSummary[]>({
        method: 'GET',
        url: `/work-orders/${workOrderId}/pegged-pos`,
    });

export const cancelWorkOrder = async (workOrderId: string): Promise<WorkOrder> =>
    ReparoApi<WorkOrder>({
        method: 'POST',
        url: `/work-orders/${workOrderId}/cancel`,
        body: JSON.stringify({}),
    });

export const updateWorkOrderStatus = async (
    workOrderId: string,
    status: WorkOrderStatus,
): Promise<WorkOrder> =>
    ReparoApi<WorkOrder>({
        method: 'POST',
        url: `/work-orders/${workOrderId}/status`,
        body: JSON.stringify({ status }),
    });

// ─── Attachments ─────────────────────────────────────────────────────────

export type WorkOrderAttachment = {
    id: string;
    workOrderId: string;
    fileName: string;
    contentType: string;
    fileSize: number;
    uploadedBy: string;
    downloadUrl?: string;
    createdAt: string;
    updatedAt: string;
};

export const uploadWorkOrderAttachment = async (
    workOrderId: string,
    file: File,
): Promise<WorkOrderAttachment> => {
    const formData = new FormData();
    formData.append('file', file);
    return ReparoApi<WorkOrderAttachment>({
        method: 'POST',
        url: `/work-orders/${workOrderId}/attachments`,
        body: formData,
    });
};

export const listWorkOrderAttachments = async (
    workOrderId: string,
): Promise<WorkOrderAttachment[]> =>
    ReparoApi<WorkOrderAttachment[]>({
        method: 'GET',
        url: `/work-orders/${workOrderId}/attachments`,
    });

export const deleteWorkOrderAttachment = async (
    workOrderId: string,
    attachmentId: string,
): Promise<void> =>
    ReparoApi<void>({
        method: 'DELETE',
        url: `/work-orders/${workOrderId}/attachments/${attachmentId}`,
    });

// ─── Picker fetches ─────────────────────────────────────────────────────
// Needed by the New WO modal's two-step location picker. Kept in this file
// because they're WO-specific usages; if other features start needing the
// same picker shapes, lift them into dedicated api/warehouse.ts / vendor.ts files.

export type WarehousePickItem = {
    warehouseId: string;
    name: string;
    /**
     * The address on record, carried so a Ship To can fill itself in. The server has always
     * sent these - the old type narrowed them away, and a comment downstream then concluded
     * "there is no address column" from the narrowed type. Blank strings are the columns'
     * NOT NULL default, so every field is optional-in-practice.
     */
    street?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    zipCode?: string | null;
};

export type VendorPickItem = {
    vendorId: string;
    name: string;
};

export const listWarehousesForPicker = async (): Promise<WarehousePickItem[]> => {
    // Restrict to INTERNAL warehouses — channel-specific warehouses (Amazon FC,
    // Zepto DS, etc.) aren't valid production locations for an in-house WO.
    const resp = await ReparoApi<{ data: WarehousePickItem[] }>({
        method: 'POST',
        url: '/warehouses/list',
        body: JSON.stringify({ fetchAll: true, types: ['INTERNAL'] }),
    });
    return (resp?.data ?? []).map((w) => ({
        warehouseId: w.warehouseId,
        name: w.name,
        street: w.street ?? null,
        city: w.city ?? null,
        state: w.state ?? null,
        country: w.country ?? null,
        zipCode: w.zipCode ?? null,
    }));
};

/**
 * Manufacturers only — a production order is handed to someone who MAKES. A pure FG / RM
 * supplier is a purchase-order counterparty and stays out; untagged vendors stay visible.
 */
export const listVendorsForPicker = async (): Promise<VendorPickItem[]> => {
    const resp = await ReparoApi<Array<{ vendorId: string; name: string; capabilities?: VendorCapability[] | null }>>({
        method: 'GET',
        url: '/vendors',
    });
    return (resp ?? []).filter(isManufacturerVendor).map((v) => ({
        vendorId: v.vendorId,
        name: v.name,
    }));
};

/**
 * The number a new production order would take under a prefix.
 *
 * A PREVIEW, NOT A CLAIM. Nothing is reserved: this is what the next number would be right now,
 * and whoever saves first takes it. Reserving on open would burn a number every time somebody
 * abandoned a form, and a gap in an order series is the thing finance asks about. If two people
 * are shown the same number, the server settles it at save and the second gets the next one.
 */
export const peekNextProductionOrderCode = async (
    prefix?: string,
    padWidth?: number,
): Promise<string> => {
    const params = new URLSearchParams();
    if (prefix) params.set('prefix', prefix);
    if (padWidth) params.set('padWidth', String(padWidth));
    const query = params.toString();

    const resp = await ReparoApi<{ code: string }>({
        method: 'GET',
        url: `/work-orders/production-orders/next-code${query ? `?${query}` : ''}`,
    });
    return resp?.code ?? '';
};
